import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import { 
  Plus, Search, Edit, Trash2, Users, Filter, CheckCircle, Clock, AlertCircle,
  Save, X, User, Building, Phone, Mail, MapPin, FileText, Eye, Upload, FileSpreadsheet,
  ArrowLeft, Loader2
} from "lucide-react";
import { motion } from "framer-motion";

import { API } from "@/config/api";
import { AREAS, REPS, getRepDisplayName, getLocationFromSuburb } from "@/config/constants";
import { RepField } from "@/components/RepField";
import LocationSelector from "@/components/LocationSelector";

const TITLES = ["Dr", "Prof", "Mr", "Mrs", "Ms", ""];

const initialFormState = {
  // Basic info - removed doctor_name (now computed from title+initials+surname)
  practice_name: "",
  city: "",
  area: "",
  suburb: "",
  doctor_phone: "",
  doctor_email: "",
  assigned_rep: "",
  // Doctor name components
  goodx_id: "",
  title: "",
  gender: "",
  initials: "",
  surname: "",
  qualification: "",
  samdc: "",  // Legacy field - kept for backward compatibility
  mp_number: "",  // New required field
  medibase: "",
  // Extended practice info
  practice_number: "",
  // Extended contact info
  cell_number: "",
  address: "",
  postal_address: ""
};

// Compute doctor display name from Title + Initials + Surname
// Falls back to surname, then doctor_name (legacy), then "Unknown Doctor"
const computeDisplayName = (doctor) => {
  if (!doctor) return "Unknown Doctor";
  
  const parts = [];
  const title = (doctor.title || "").trim();
  const initials = (doctor.initials || "").trim();
  const surname = (doctor.surname || "").trim();
  
  if (title && title !== "none") parts.push(title);
  if (initials) parts.push(initials);
  if (surname) parts.push(surname);
  
  if (parts.length > 0) return parts.join(" ");
  
  // Fallback to legacy doctor_name
  if (doctor.doctor_name) return doctor.doctor_name;
  
  // Final fallback: use display_name from API if available
  if (doctor.display_name) return doctor.display_name;
  
  return "Unknown Doctor";
};

// Form field component for consistency - Touch-friendly on mobile
const FormField = ({ label, id, value, onChange, type = "text", required = false, placeholder = "", disabled = false, error = "" }) => (
  <div className="space-y-1">
    <Label htmlFor={id} className="text-xs text-muted-foreground">
      {label} {required && <span className="text-red-500">*</span>}
    </Label>
    <Input
      id={id}
      type={type}
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      required={required}
      placeholder={placeholder}
      disabled={disabled}
      className={`h-11 sm:h-9 ${error ? "border-red-500" : ""}`}
    />
    {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
  </div>
);

// Section header component
const SectionHeader = ({ icon: Icon, title }) => (
  <div className="flex items-center gap-2 mb-2 sm:mb-3">
    <Icon className="w-4 h-4 text-primary" />
    <h3 className="text-xs sm:text-sm font-semibold text-foreground">{title}</h3>
  </div>
);

const DoctorsPage = ({ user }) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  
  const [doctors, setDoctors] = useState([]);
  const [masterDoctors, setMasterDoctors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterArea, setFilterArea] = useState("");
  const [filterRep, setFilterRep] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [createdDoctor, setCreatedDoctor] = useState(null);
  const [editingDoctor, setEditingDoctor] = useState(null);
  const [formData, setFormData] = useState(initialFormState);
  const [submitting, setSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState("all");
  
  // Doctor detail sheet state
  const [selectedDoctor, setSelectedDoctor] = useState(null);
  const [detailSheetOpen, setDetailSheetOpen] = useState(false);
  const [isEditingDetail, setIsEditingDetail] = useState(false);
  const [detailFormData, setDetailFormData] = useState(initialFormState);
  
  // Import dialog state (managers only)
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResults, setImportResults] = useState(null);
  const [updateMissingOnly, setUpdateMissingOnly] = useState(false);
  
  // Form validation state
  const [formErrors, setFormErrors] = useState({});
  
  const validateForm = (data) => {
    const errors = {};
    if (!data.title || data.title === "none" || data.title === "") errors.title = "Title is required";
    if (!data.initials?.trim()) errors.initials = "Name / Initials is required";
    if (!data.surname?.trim()) errors.surname = "Surname is required";
    if (!data.goodx_id?.trim()) errors.goodx_id = "GoodX ID is required";
    if (!data.qualification?.trim()) errors.qualification = "Speciality is required";
    if (!data.mp_number?.trim()) errors.mp_number = "MP Number is required";
    if (!data.address?.trim()) errors.address = "Address is required";
    if (!data.doctor_email?.trim()) errors.doctor_email = "Email is required";
    if (!data.practice_name?.trim()) errors.practice_name = "Practice Name is required";
    if (!data.practice_number?.trim()) errors.practice_number = "Practice Number is required";
    if (!data.cell_number?.trim()) errors.cell_number = "Contact Number is required";
    if (!data.suburb?.trim()) errors.suburb = "Suburb is required";
    if (!data.gender?.trim()) errors.gender = "Gender is required";
    if (!data.assigned_rep?.trim()) errors.assigned_rep = "Assigned Rep is required";
    return errors;
  };

  const clearFieldError = (field) => {
    if (formErrors[field]) {
      setFormErrors(prev => { const n = { ...prev }; delete n[field]; return n; });
    }
  };
  
  // Return URL handling (for "New Doctor" redirect workflow)
  const returnTo = searchParams.get("returnTo");
  const returnContext = searchParams.get("context"); // e.g., "calendar", "visit"
  
  // RBAC helpers
  const isRep = user?.role === "rep";
  const isManager = user?.role === "manager" || user?.role === "admin";
  const allowedAreas = user?.allowed_areas || [];
  

  useEffect(() => {
    fetchDoctors();
    fetchMasterDoctors();
  }, [search, filterArea, filterRep]);

  const fetchDoctors = async () => {
    try {
      const params = new URLSearchParams();
      if (search) params.append("search", search);
      if (filterArea && filterArea !== "all") params.append("area", filterArea);
      if (filterRep && filterRep !== "all") params.append("rep", filterRep);
      params.append("active_only", "false");
      
      const response = await axios.get(`${API}/doctors?${params.toString()}`);
      setDoctors(response.data);
    } catch (error) {
      toast.error("Failed to fetch doctors");
    } finally {
      setLoading(false);
    }
  };

  const fetchMasterDoctors = async () => {
    try {
      const response = await axios.get(`${API}/master-doctors`);
      setMasterDoctors(response.data);
    } catch (error) {
      console.error("Failed to fetch master doctors");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Prepare form data first for validation
    let submitData = { ...formData };
    if (isRep) {
      if (user?.assigned_rep_name) submitData.assigned_rep = user.assigned_rep_name;
      if (allowedAreas.length === 1 && !submitData.area) submitData.area = allowedAreas[0];
    }
    
    // Validate
    const errors = validateForm(submitData);
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      toast.error("Please fill in all required fields");
      return;
    }
    setFormErrors({});
    setSubmitting(true);

    try {
      // Compute doctor_name from title + initials + surname for backward compatibility
      const displayParts = [];
      if (submitData.title && submitData.title !== "none") displayParts.push(submitData.title);
      if (submitData.initials) displayParts.push(submitData.initials);
      if (submitData.surname) displayParts.push(submitData.surname);
      submitData.doctor_name = displayParts.join(" ") || submitData.surname || "";
      
      if (editingDoctor) {
        await axios.put(`${API}/doctors/${editingDoctor.doctor_id}`, submitData);
        toast.success("Doctor updated successfully");
      } else {
        const dupCheck = await axios.post(`${API}/doctors/check-duplicate`, submitData);
        if (dupCheck.data.is_duplicate) {
          toast.error("A doctor with matching identifiers already exists");
          setSubmitting(false);
          return;
        }
        const response = await axios.post(`${API}/doctors`, submitData);
        toast.success("Doctor added successfully");
        
        // Handle return navigation (for "New Doctor" redirect workflow)
        if (returnTo && response.data) {
          const newDoctor = response.data;
          // Build return URL with new doctor info - use computed display name
          const displayName = computeDisplayName(newDoctor);
          const returnUrl = new URL(returnTo, window.location.origin);
          returnUrl.searchParams.set("doctor_id", newDoctor.doctor_id);
          returnUrl.searchParams.set("doctor_name", displayName);
          returnUrl.searchParams.set("practice_name", newDoctor.practice_name || "");
          returnUrl.searchParams.set("area", newDoctor.area || "");
          returnUrl.searchParams.set("new_doctor_created", "true");

          setDialogOpen(false);
          setFormData(initialFormState);
          setEditingDoctor(null);
          fetchDoctors();
          fetchMasterDoctors();

          // Ask before jumping back, so the rep stays in control
          setCreatedDoctor({
            name: displayName,
            area: newDoctor.area || "",
            url: returnUrl.pathname + returnUrl.search,
          });
          return;
        }
      }
      setDialogOpen(false);
      setFormData(initialFormState);
      setEditingDoctor(null);
      fetchDoctors();
      fetchMasterDoctors();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save doctor");
    } finally {
      setSubmitting(false);
    }
  };

  // Check if current user can delete (manager or admin only)
  const canDelete = user?.role === "manager" || user?.role === "admin";

  const handleDelete = async (doctorId) => {
    // Double-check permission (UI might be cached)
    if (!canDelete) {
      toast.error("You don't have permission to delete records. Please contact management.");
      return;
    }
    
    if (!window.confirm("Are you sure you want to deactivate this doctor?")) return;
    
    try {
      await axios.delete(`${API}/doctors/${doctorId}`, { withCredentials: true });
      toast.success("Doctor deactivated");
      fetchDoctors();
      if (selectedDoctor?.doctor_id === doctorId) {
        setDetailSheetOpen(false);
      }
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error("You don't have permission to delete records. Please contact management.");
      } else {
        toast.error("Failed to deactivate doctor");
      }
    }
  };

  const handleReactivate = async (doctorId) => {
    try {
      await axios.put(`${API}/doctors/${doctorId}`, { active: true });
      toast.success("Doctor reactivated");
      fetchDoctors();
    } catch (error) {
      toast.error("Failed to reactivate doctor");
    }
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setFormData(initialFormState);
    setEditingDoctor(null);
    setFormErrors({});
    
    // If user was redirected here to add a doctor, navigate back without creating
    if (returnTo) {
      navigate(returnTo);
    }
  };
  
  // Open Add Doctor dialog with RBAC pre-fill
  const handleAddDoctorClick = () => {
    // For reps with one allowed area, pre-set the area
    if (isRep && user?.assigned_rep_name) {
      setFormData({ ...initialFormState, assigned_rep: user?.assigned_rep_name || "" });
    } else if (isRep && user?.assigned_rep_name) {
      setFormData({ ...initialFormState, assigned_rep: user?.assigned_rep_name || "" });
    } else {
      setFormData(initialFormState);
    }
    setEditingDoctor(null);
    setDialogOpen(true);
  };
  
  // Auto-open Add Doctor dialog when redirected from calendar/visit pages
  useEffect(() => {
    if (returnTo && returnContext) {
      // User was redirected here to create a new doctor
      toast.info(`Please add the new doctor first, then you'll be redirected back to ${returnContext === "calendar" ? "plan your appointment" : "log your visit"}.`, {
        duration: 5000
      });
      handleAddDoctorClick();
    }
  }, [returnTo, returnContext]);
  
  // ============ Import Functions (Managers Only) ============
  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const ext = file.name.toLowerCase().split(".").pop();
      if (!["xlsx", "xls", "csv"].includes(ext)) {
        toast.error("Please select an Excel (.xlsx, .xls) or CSV file");
        return;
      }
      setImportFile(file);
      setImportResults(null);
    }
  };
  
  const handleImport = async () => {
    if (!importFile) {
      toast.error("Please select a file first");
      return;
    }
    
    setImporting(true);
    setImportResults(null);
    
    try {
      const formData = new FormData();
      formData.append("file", importFile);
      
      const response = await axios.post(
        `${API}/doctors/import?update_missing_only=${updateMissingOnly}`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
          withCredentials: true
        }
      );
      
      setImportResults(response.data);
      
      if (response.data.imported > 0 || response.data.updated > 0) {
        toast.success(`Import complete: ${response.data.imported} new doctors, ${response.data.updated} updated`);
        fetchDoctors();
        fetchMasterDoctors();
      } else {
        toast.info("No new doctors imported - all records were duplicates or had missing required fields");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Import failed");
      setImportResults({ error: error.response?.data?.detail || "Import failed" });
    } finally {
      setImporting(false);
    }
  };
  
  const resetImportDialog = () => {
    setImportFile(null);
    setImportResults(null);
    setUpdateMissingOnly(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  // Open doctor detail sheet
  const handleViewDoctor = (doctor) => {
    setSelectedDoctor(doctor);
    // Resolve suburb → city/area from new hierarchy for legacy doctors
    let resolvedCity = doctor.city || "";
    let resolvedArea = doctor.area || "";
    if (doctor.suburb && !doctor.city) {
      const resolved = getLocationFromSuburb(doctor.suburb);
      if (resolved) {
        resolvedCity = resolved.city;
        resolvedArea = resolved.area;
      }
    }
    setDetailFormData({
      practice_name: doctor.practice_name || "",
      city: resolvedCity,
      area: resolvedArea,
      suburb: doctor.suburb || "",
      doctor_phone: doctor.doctor_phone || doctor.tel_number || "",
      doctor_email: doctor.doctor_email || doctor.email || "",
      assigned_rep: doctor.assigned_rep || "",
      goodx_id: doctor.goodx_id || "",
      title: doctor.title || "",
      gender: doctor.gender || "",
      initials: doctor.initials || "",
      surname: doctor.surname || "",
      qualification: doctor.qualification || "",
      samdc: doctor.samdc || doctor.samdc_number || "",  // Legacy field
      mp_number: doctor.mp_number || "",  // New required field
      medibase: doctor.medibase || "",
      practice_number: doctor.practice_number || "",
      cell_number: doctor.cell_number || "",
      address: doctor.address || "",
      postal_address: doctor.postal_address || ""
    });
    setIsEditingDetail(false);
    setDetailSheetOpen(true);
  };

  // Save doctor detail edits
  const handleSaveDetail = async () => {
    const errors = validateForm(detailFormData);
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      toast.error("Please fill in all required fields");
      return;
    }
    setFormErrors({});
    setSubmitting(true);
    try {
      await axios.put(`${API}/doctors/${selectedDoctor.doctor_id}`, detailFormData);
      toast.success("Doctor updated successfully");
      setIsEditingDetail(false);
      fetchDoctors();
      // Update selected doctor with new data
      setSelectedDoctor({ ...selectedDoctor, ...detailFormData });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update doctor");
    } finally {
      setSubmitting(false);
    }
  };


  const getFilteredDoctors = () => {
    let filtered = doctors;
    
    if (activeTab === "active") {
      filtered = doctors.filter(d => d.active);
    } else if (activeTab === "inactive") {
      filtered = doctors.filter(d => !d.active);
    } else if (activeTab === "synced") {
      const masterEmails = new Set(masterDoctors.map(m => m.email));
      filtered = doctors.filter(d => masterEmails.has(d.doctor_email));
    }
    
    return filtered;
  };

  const filteredDoctors = getFilteredDoctors();
  const activeDoctorsCount = doctors.filter(d => d.active).length;
  const inactiveDoctorsCount = doctors.filter(d => !d.active).length;
  const syncedCount = masterDoctors.length;

  return (
    <div className="p-4 lg:p-6 space-y-3 sm:space-y-4 pb-6" data-testid="doctors-page">
      {/* Return navigation banner - shown when redirected from calendar/visit */}
      {returnTo && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-center justify-between">
          <div className="flex items-center gap-2 text-blue-800">
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm">Add a new doctor to continue with your {returnContext === "calendar" ? "appointment" : "visit"}</span>
          </div>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => navigate(returnTo)}
            className="text-blue-700 hover:text-blue-900"
          >
            Cancel & Go Back
          </Button>
        </div>
      )}
      
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-lg sm:text-xl lg:text-2xl font-bold text-foreground" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Doctors
          </h1>
          <p className="text-xs sm:text-sm text-muted-foreground">Manage your doctor database</p>
        </div>
        
        <div className="flex flex-col sm:flex-row gap-2">
          {/* Import Button - Managers Only */}
          {isManager && (
            <Dialog open={importDialogOpen} onOpenChange={(open) => { 
              setImportDialogOpen(open); 
              if (!open) resetImportDialog();
            }}>
              <DialogTrigger asChild>
                <Button variant="outline" className="gap-2 w-full sm:w-auto h-10" data-testid="import-doctors-button">
                  <Upload className="w-4 h-4" />
                  Import
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <FileSpreadsheet className="w-5 h-5 text-emerald-600" />
                    Import Doctors from Excel
                  </DialogTitle>
                  <DialogDescription>
                    Upload an Excel (.xlsx) or CSV file to bulk import doctors. Duplicates will be automatically detected.
                  </DialogDescription>
                </DialogHeader>
                
                <div className="space-y-4 py-4">
                  {/* File Input */}
                  <div className="space-y-2">
                    <Label>Select File</Label>
                    <Input
                      ref={fileInputRef}
                      type="file"
                      accept=".xlsx,.xls,.csv"
                      onChange={handleFileSelect}
                      className="cursor-pointer"
                      data-testid="import-file-input"
                    />
                    {importFile && (
                      <p className="text-sm text-muted-foreground flex items-center gap-2">
                        <FileSpreadsheet className="w-4 h-4" />
                        {importFile.name}
                      </p>
                    )}
                  </div>
                  
                  {/* Update Missing Only Option */}
                  <div className="flex items-center gap-3 py-2">
                    <input
                      type="checkbox"
                      id="updateMissingOnly"
                      checked={updateMissingOnly}
                      onChange={(e) => setUpdateMissingOnly(e.target.checked)}
                      className="w-4 h-4 rounded border-gray-300"
                    />
                    <Label htmlFor="updateMissingOnly" className="text-sm font-normal cursor-pointer">
                      Only update empty fields on existing doctors (don&apos;t skip duplicates)
                    </Label>
                  </div>
                  
                  {/* Dedupe Rule Info */}
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <p className="text-xs text-blue-700">
                      <span className="font-medium">Required columns:</span> Surname, MP Number. 
                      Dedupe matches by: MP Number only. Shared emails, phones, and practice details are allowed.
                    </p>
                  </div>
                  
                  {/* Import Results */}
                  {importResults && !importResults.error && (
                    <div className="bg-slate-50 rounded-lg p-4 space-y-2">
                      <h4 className="font-medium text-sm">Import Results</h4>
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Total Rows:</span>
                          <span className="font-medium">{importResults.total_rows}</span>
                        </div>
                        <div className="flex justify-between text-emerald-700">
                          <span>Imported:</span>
                          <span className="font-medium">{importResults.imported}</span>
                        </div>
                        <div className="flex justify-between text-blue-700">
                          <span>Updated:</span>
                          <span className="font-medium">{importResults.updated}</span>
                        </div>
                        <div className="flex justify-between text-amber-700">
                          <span>Duplicates:</span>
                          <span className="font-medium">{importResults.skipped_duplicates}</span>
                        </div>
                        {importResults.shared_emails_detected > 0 && (
                          <div className="flex justify-between text-purple-700 col-span-2">
                            <span>Shared Emails:</span>
                            <span className="font-medium">{importResults.shared_emails_detected} doctors</span>
                          </div>
                        )}
                        {importResults.skipped_errors > 0 && (
                          <div className="flex justify-between text-red-700 col-span-2">
                            <span>Errors:</span>
                            <span className="font-medium">{importResults.skipped_errors}</span>
                          </div>
                        )}
                      </div>
                      {importResults.lenient_mode && (
                        <p className="text-xs text-amber-600 mt-2 italic">
                          * Imported using Lenient Mode (shared emails allowed)
                        </p>
                      )}
                      {importResults.errors?.length > 0 && (
                        <div className="mt-2 text-xs text-red-600 max-h-20 overflow-y-auto">
                          {importResults.errors.slice(0, 5).map((err, i) => (
                            <p key={i}>{err}</p>
                          ))}
                          {importResults.errors.length > 5 && (
                            <p className="text-muted-foreground">...and {importResults.errors.length - 5} more errors</p>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                  
                  {importResults?.error && (
                    <div className="bg-red-50 text-red-700 rounded-lg p-3 text-sm">
                      {importResults.error}
                    </div>
                  )}
                </div>
                
                <DialogFooter>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setImportDialogOpen(false);
                      resetImportDialog();
                    }}
                  >
                    {importResults ? "Close" : "Cancel"}
                  </Button>
                  {!importResults && (
                    <Button
                      onClick={handleImport}
                      disabled={!importFile || importing}
                      className="gap-2"
                      data-testid="import-submit-button"
                    >
                      {importing ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Importing...
                        </>
                      ) : (
                        <>
                          <Upload className="w-4 h-4" />
                          Import Doctors
                        </>
                      )}
                    </Button>
                  )}
                </DialogFooter>
              </DialogContent>
            </Dialog>
          )}
          
          {/* Recover MP Numbers - Hidden, kept for future use */}
          {false && isManager && (
            <Button
              variant="outline"
              size="sm"
              onClick={async () => {
                try {
                  const response = await axios.post(`${API}/admin/migrate-samdc-to-mp-number`);
                  toast.success(`MP Number recovery complete: ${response.data.updated} doctors updated`);
                  fetchDoctors();
                } catch (error) {
                  toast.error("Migration failed: " + (error.response?.data?.detail || error.message));
                }
              }}
              className="gap-2"
              data-testid="btn-migrate-samdc"
            >
              Recover MP Numbers
            </Button>
          )}

          {/* Add Doctor Dialog */}
          <Dialog open={dialogOpen} onOpenChange={(open) => { if (!open) handleDialogClose(); else handleAddDoctorClick(); }}>
            <DialogTrigger asChild>
              <Button className="gap-2 w-full sm:w-auto h-10" data-testid="add-doctor-button">
                <Plus className="w-4 h-4" />
                Add Doctor
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto mx-4 sm:mx-auto">
              <DialogHeader>
                <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
                  {editingDoctor ? "Edit Doctor" : "Add New Doctor"}
                  {returnTo && !editingDoctor && (
                    <Badge variant="secondary" className="ml-2 bg-blue-100 text-blue-800">
                      New Doctor
                    </Badge>
                  )}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6" data-testid="doctor-form">
                {/* Doctor Information Section */}
                <div className="space-y-3">
                  <SectionHeader icon={User} title="Doctor Information" />
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <FormField
                    label="GoodX ID"
                    id="goodx_id"
                    value={formData.goodx_id}
                    onChange={(v) => { setFormData({ ...formData, goodx_id: v }); clearFieldError("goodx_id"); }}
                    placeholder="GX-XXXXX"
                    error={formErrors.goodx_id}
                  />
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">Title</Label>
                    <Select
                      value={formData.title}
                      onValueChange={(v) => { setFormData({ ...formData, title: v }); clearFieldError("title"); }}
                    >
                      <SelectTrigger className={`h-11 sm:h-9 ${formErrors.title ? "border-red-500" : ""}`}>
                        <SelectValue placeholder="Title" />
                      </SelectTrigger>
                      <SelectContent>
                        {TITLES.map((t) => (
                          <SelectItem key={t || "none"} value={t || "none"}>{t || "None"}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {formErrors.title && <p className="text-xs text-red-500 mt-1">{formErrors.title}</p>}
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Gender</Label>
                    <div className={`flex gap-3 mt-1 ${formErrors.gender ? "ring-2 ring-red-500 rounded-lg" : ""}`}>
                      <button
                        type="button"
                        onClick={() => { setFormData({...formData, gender: "Male"}); clearFieldError("gender"); }}
                        className={`flex-1 py-2 rounded-lg border-2 text-sm font-medium transition-colors ${
                          formData.gender === "Male"
                            ? "border-primary bg-primary text-white"
                            : "border-gray-200 bg-white text-gray-700 hover:border-primary/50"
                        }`}
                      >
                        Male
                      </button>
                      <button
                        type="button"
                        onClick={() => { setFormData({...formData, gender: "Female"}); clearFieldError("gender"); }}
                        className={`flex-1 py-2 rounded-lg border-2 text-sm font-medium transition-colors ${
                          formData.gender === "Female"
                            ? "border-primary bg-primary text-white"
                            : "border-gray-200 bg-white text-gray-700 hover:border-primary/50"
                        }`}
                      >
                        Female
                      </button>
                    </div>
                    {formErrors.gender && <p className="text-xs text-red-500 mt-1">{formErrors.gender}</p>}
                  </div>
                  <FormField
                    label="Initials"
                    id="initials"
                    value={formData.initials}
                    onChange={(v) => { setFormData({ ...formData, initials: v }); clearFieldError("initials"); }}
                    required
                    placeholder="Required"
                    error={formErrors.initials}
                  />
                  <FormField
                    label="Surname"
                    id="surname"
                    value={formData.surname}
                    onChange={(v) => { setFormData({ ...formData, surname: v }); clearFieldError("surname"); }}
                    required
                    placeholder="Required"
                    error={formErrors.surname}
                  />
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  <FormField
                    label="MP Number"
                    id="mp_number"
                    value={formData.mp_number}
                    onChange={(v) => { setFormData({ ...formData, mp_number: v }); clearFieldError("mp_number"); }}
                    required
                    placeholder="Required"
                    error={formErrors.mp_number}
                  />
                  <FormField
                    label="Speciality"
                    id="qualification"
                    value={formData.qualification}
                    onChange={(v) => { setFormData({ ...formData, qualification: v }); clearFieldError("qualification"); }}
                    placeholder="e.g., General Practitioner, Cardiologist"
                    error={formErrors.qualification}
                  />
                  <FormField
                    label="Medibase"
                    id="medibase"
                    value={formData.medibase}
                    onChange={(v) => setFormData({ ...formData, medibase: v })}
                  />
                </div>
                <LocationSelector
                  city={formData.city}
                  area={formData.area}
                  suburb={formData.suburb}
                  onLocationChange={(location) => {
                    setFormData({
                      ...formData,
                      city: location.city,
                      area: location.area,
                      suburb: location.suburb
                    });
                    clearFieldError("suburb");
                  }}
                  required={true}
                  showError={!!formErrors.suburb}
                  data-testid="doctor-form-location-selector"
                />
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">Assigned Rep {isManager && <span className="text-red-500">*</span>}</Label>
                    {isRep ? (
                      <RepField user={user} testId="select-assigned-rep" />
                    ) : (
                      <Select
                        value={formData.assigned_rep}
                        onValueChange={(v) => { setFormData({ ...formData, assigned_rep: v }); clearFieldError("assigned_rep"); }}
                        required={isManager}
                      >
                        <SelectTrigger className={`h-9 ${formErrors.assigned_rep ? "border-red-500" : ""}`} data-testid="select-assigned-rep">
                          <SelectValue placeholder="Select rep" />
                        </SelectTrigger>
                        <SelectContent>
                          {REPS.map((rep) => (
                            <SelectItem key={rep} value={rep}>{getRepDisplayName(rep)}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                    {formErrors.assigned_rep && <p className="text-xs text-red-500 mt-1">{formErrors.assigned_rep}</p>}
                  </div>
                </div>
              </div>

              <Separator />

              {/* Practice Information Section */}
              <div className="space-y-3">
                <SectionHeader icon={Building} title="Practice Information" />
                <div className="grid grid-cols-2 gap-3">
                  <FormField
                    label="Practice Name"
                    id="practice_name"
                    value={formData.practice_name}
                    onChange={(v) => { setFormData({ ...formData, practice_name: v }); clearFieldError("practice_name"); }}
                    required
                    error={formErrors.practice_name}
                  />
                  <FormField
                    label="Practice Number"
                    id="practice_number"
                    value={formData.practice_number}
                    onChange={(v) => { setFormData({ ...formData, practice_number: v }); clearFieldError("practice_number"); }}
                    error={formErrors.practice_number}
                  />
                </div>
              </div>

              <Separator />

              {/* Contact Information Section */}
              <div className="space-y-3">
                <SectionHeader icon={Phone} title="Contact Information" />
                <div className="grid grid-cols-2 gap-3">
                  <FormField
                    label="Office Phone"
                    id="doctor_phone"
                    type="tel"
                    value={formData.doctor_phone}
                    onChange={(v) => setFormData({ ...formData, doctor_phone: v })}
                    required
                  />
                  <FormField
                    label="Cell Number"
                    id="cell_number"
                    type="tel"
                    value={formData.cell_number}
                    onChange={(v) => { setFormData({ ...formData, cell_number: v }); clearFieldError("cell_number"); }}
                    error={formErrors.cell_number}
                  />
                </div>
                <FormField
                  label="Email"
                  id="doctor_email"
                  type="email"
                  value={formData.doctor_email}
                  onChange={(v) => { setFormData({ ...formData, doctor_email: v }); clearFieldError("doctor_email"); }}
                  required
                  error={formErrors.doctor_email}
                />
              </div>

              <Separator />

              {/* Address Section */}
              <div className="space-y-3">
                <SectionHeader icon={MapPin} title="Address" />
                <FormField
                  label="Physical Address"
                  id="address"
                  value={formData.address}
                  onChange={(v) => { setFormData({ ...formData, address: v }); clearFieldError("address"); }}
                  error={formErrors.address}
                />
                <FormField
                  label="Postal Address"
                  id="postal_address"
                  value={formData.postal_address}
                  onChange={(v) => setFormData({ ...formData, postal_address: v })}
                />
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button type="button" variant="outline" onClick={handleDialogClose}>
                  Cancel
                </Button>
                <Button type="submit" disabled={submitting} data-testid="submit-doctor-button">
                  {submitting ? "Saving..." : editingDoctor ? "Update" : "Add Doctor"}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Card className="cursor-pointer hover:border-primary/50" onClick={() => setActiveTab("all")}>
          <CardContent className="p-3">
            <div className="flex items-center gap-2">
              <Users className="w-6 h-6 text-slate-400" />
              <div>
                <p className="text-xl font-bold">{doctors.length}</p>
                <p className="text-xs text-muted-foreground">Total</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:border-emerald-300" onClick={() => setActiveTab("active")}>
          <CardContent className="p-3">
            <div className="flex items-center gap-2">
              <CheckCircle className="w-6 h-6 text-emerald-500" />
              <div>
                <p className="text-xl font-bold">{activeDoctorsCount}</p>
                <p className="text-xs text-muted-foreground">Active</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:border-slate-300" onClick={() => setActiveTab("inactive")}>
          <CardContent className="p-3">
            <div className="flex items-center gap-2">
              <AlertCircle className="w-6 h-6 text-slate-400" />
              <div>
                <p className="text-xl font-bold">{inactiveDoctorsCount}</p>
                <p className="text-xs text-muted-foreground">Inactive</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="cursor-pointer hover:border-blue-300" onClick={() => setActiveTab("synced")}>
          <CardContent className="p-3">
            <div className="flex items-center gap-2">
              <FileText className="w-6 h-6 text-blue-500" />
              <div>
                <p className="text-xl font-bold">{syncedCount}</p>
                <p className="text-xs text-muted-foreground">Master List</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-3">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search doctors..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9 h-9"
                data-testid="search-doctors"
              />
            </div>
            <div className="flex gap-2">
              <Select value={filterArea} onValueChange={setFilterArea}>
                <SelectTrigger className="w-28 h-9">
                  <SelectValue placeholder="Area" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Areas</SelectItem>
                  {AREAS.map((area) => (
                    <SelectItem key={area} value={area}>{area}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={filterRep} onValueChange={setFilterRep}>
                <SelectTrigger className="w-28 h-9">
                  <SelectValue placeholder="Rep" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Reps</SelectItem>
                  {REPS.map((rep) => (
                    <SelectItem key={rep} value={rep}>{getRepDisplayName(rep)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Tabs for different views */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="all">All</TabsTrigger>
          <TabsTrigger value="active">Active</TabsTrigger>
          <TabsTrigger value="inactive">Inactive</TabsTrigger>
          <TabsTrigger value="synced">Master</TabsTrigger>
        </TabsList>

        <TabsContent value={activeTab} className="mt-3">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
          >
            <Card>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Doctor ID</TableHead>
                        <TableHead>Name</TableHead>
                        <TableHead>Practice</TableHead>
                        <TableHead>Area</TableHead>
                        <TableHead>Phone</TableHead>
                        <TableHead>Rep</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {loading ? (
                        <TableRow>
                          <TableCell colSpan={8} className="text-center py-8">
                            <div className="w-6 h-6 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto"></div>
                          </TableCell>
                        </TableRow>
                      ) : filteredDoctors.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={8} className="text-center py-8">
                            <Users className="w-10 h-10 text-muted-foreground/50 mx-auto mb-2" />
                            <p className="text-sm text-muted-foreground">No doctors found</p>
                          </TableCell>
                        </TableRow>
                      ) : (
                        filteredDoctors.map((doctor) => (
                          <TableRow 
                            key={doctor.doctor_id} 
                            className="cursor-pointer hover:bg-muted/50"
                            onClick={() => handleViewDoctor(doctor)}
                            data-testid={`doctor-row-${doctor.doctor_id}`}
                          >
                            <TableCell className="font-mono text-xs">{doctor.doctor_id}</TableCell>
                            <TableCell className="font-medium">
                              {computeDisplayName(doctor)}
                            </TableCell>
                            <TableCell className="text-sm">{doctor.practice_name || doctor.practice_number}</TableCell>
                            <TableCell>
                              <span className="text-sm">
                                {doctor.city && doctor.area && doctor.suburb
                                  ? `${doctor.city} / ${doctor.area} / ${doctor.suburb}`
                                  : doctor.area || "—"}
                              </span>
                            </TableCell>
                            <TableCell className="text-sm">{doctor.doctor_phone || doctor.tel_number}</TableCell>
                            <TableCell>
                              <Badge variant="outline">Rep {doctor.assigned_rep}</Badge>
                            </TableCell>
                            <TableCell>
                              <Badge 
                                variant="secondary" 
                                className={doctor.active ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-600"}
                              >
                                {doctor.active ? "Active" : "Inactive"}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-right">
                              <div className="flex justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-8 w-8"
                                  onClick={(e) => { e.stopPropagation(); handleViewDoctor(doctor); }}
                                  data-testid={`view-doctor-${doctor.doctor_id}`}
                                >
                                  <Eye className="w-4 h-4" />
                                </Button>
                                {/* Only show delete for managers/admins */}
                                {canDelete && doctor.active && (
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-8 w-8 text-destructive hover:text-destructive"
                                    onClick={(e) => { e.stopPropagation(); handleDelete(doctor.doctor_id); }}
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                )}
                                {/* Reactivate button for managers/admins on inactive doctors */}
                                {canDelete && !doctor.active && (
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-8 w-8 text-emerald-600"
                                    onClick={(e) => { e.stopPropagation(); handleReactivate(doctor.doctor_id); }}
                                  >
                                    <CheckCircle className="w-4 h-4" />
                                  </Button>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </TabsContent>
      </Tabs>

      {/* Doctor Detail Sheet - Full screen on mobile */}
      <Sheet open={detailSheetOpen} onOpenChange={setDetailSheetOpen}>
        <SheetContent className="w-full sm:max-w-xl overflow-y-auto p-4 sm:p-6" data-testid="doctor-detail-sheet">
          <SheetHeader className="mb-3 sm:mb-4">
            <div className="flex items-center justify-between gap-2">
              <div>
                <SheetTitle className="text-base sm:text-lg" style={{ fontFamily: 'Manrope, sans-serif' }}>
                  {computeDisplayName(selectedDoctor)}
                </SheetTitle>
                <p className="text-xs text-muted-foreground">{selectedDoctor?.practice_name || selectedDoctor?.practice_number || ""}</p>
              </div>
              <div className="flex gap-2">
                {isEditingDetail ? (
                  <>
                    <Button variant="outline" size="sm" onClick={() => { setIsEditingDetail(false); setFormErrors({}); }} className="h-9 px-2 sm:px-3">
                      <X className="w-4 h-4 sm:mr-1" /> <span className="hidden sm:inline">Cancel</span>
                    </Button>
                    <Button size="sm" onClick={handleSaveDetail} disabled={submitting} className="h-9 px-2 sm:px-3">
                      <Save className="w-4 h-4 sm:mr-1" /> <span className="hidden sm:inline">{submitting ? "Saving..." : "Save"}</span>
                    </Button>
                  </>
                ) : (
                  <Button variant="outline" size="sm" onClick={() => setIsEditingDetail(true)} className="h-9">
                    <Edit className="w-4 h-4 mr-1" /> Edit
                  </Button>
                )}
              </div>
            </div>
          </SheetHeader>

          {selectedDoctor && (
            <ScrollArea className="h-[calc(100vh-140px)] sm:h-[calc(100vh-120px)] pr-2 sm:pr-4">
              <div className="space-y-4 sm:space-y-6">
                {/* Doctor Information */}
                <div className="space-y-3">
                  <SectionHeader icon={User} title="Doctor Information" />
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <FormField
                      label="GoodX ID"
                      id="detail_goodx_id"
                      value={detailFormData.goodx_id}
                      onChange={(v) => { setDetailFormData({ ...detailFormData, goodx_id: v }); clearFieldError("goodx_id"); }}
                      disabled={!isEditingDetail}
                      error={isEditingDetail ? formErrors.goodx_id : ""}
                    />
                    {isEditingDetail ? (
                      <div className="space-y-1">
                        <Label className="text-xs text-muted-foreground">Title</Label>
                        <Select
                          value={detailFormData.title || "none"}
                          onValueChange={(v) => { setDetailFormData({ ...detailFormData, title: v === "none" ? "" : v }); clearFieldError("title"); }}
                        >
                          <SelectTrigger className={`h-11 sm:h-9 ${formErrors.title ? "border-red-500" : ""}`}>
                            <SelectValue placeholder="Title" />
                          </SelectTrigger>
                          <SelectContent>
                            {TITLES.map((t) => (
                              <SelectItem key={t || "none"} value={t || "none"}>{t || "None"}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {formErrors.title && <p className="text-xs text-red-500 mt-1">{formErrors.title}</p>}
                      </div>
                    ) : (
                      <FormField
                        label="Title"
                        id="detail_title"
                        value={detailFormData.title}
                        onChange={() => {}}
                        disabled
                      />
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    {isEditingDetail ? (
                      <div>
                        <Label className="text-xs text-muted-foreground">Gender</Label>
                        <div className={`flex gap-3 mt-1 ${formErrors.gender ? "ring-2 ring-red-500 rounded-lg" : ""}`}>
                          <button
                            type="button"
                            onClick={() => { setDetailFormData({...detailFormData, gender: "Male"}); clearFieldError("gender"); }}
                            className={`flex-1 py-2 rounded-lg border-2 text-sm font-medium transition-colors ${
                              detailFormData.gender === "Male"
                                ? "border-primary bg-primary text-white"
                                : "border-gray-200 bg-white text-gray-700 hover:border-primary/50"
                            }`}
                          >
                            Male
                          </button>
                          <button
                            type="button"
                            onClick={() => { setDetailFormData({...detailFormData, gender: "Female"}); clearFieldError("gender"); }}
                            className={`flex-1 py-2 rounded-lg border-2 text-sm font-medium transition-colors ${
                              detailFormData.gender === "Female"
                                ? "border-primary bg-primary text-white"
                                : "border-gray-200 bg-white text-gray-700 hover:border-primary/50"
                            }`}
                          >
                            Female
                          </button>
                        </div>
                        {formErrors.gender && <p className="text-xs text-red-500 mt-1">{formErrors.gender}</p>}
                      </div>
                    ) : (
                      <FormField
                        label="Gender"
                        id="detail_gender"
                        value={detailFormData.gender}
                        onChange={() => {}}
                        disabled
                      />
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <FormField
                      label="Initials"
                      id="detail_initials"
                      value={detailFormData.initials}
                      onChange={(v) => { setDetailFormData({ ...detailFormData, initials: v }); clearFieldError("initials"); }}
                      disabled={!isEditingDetail}
                      error={isEditingDetail ? formErrors.initials : ""}
                    />
                    <FormField
                      label="Surname"
                      id="detail_surname"
                      value={detailFormData.surname}
                      onChange={(v) => { setDetailFormData({ ...detailFormData, surname: v }); clearFieldError("surname"); }}
                      disabled={!isEditingDetail}
                      required
                      error={isEditingDetail ? formErrors.surname : ""}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <FormField
                      label="Speciality"
                      id="detail_qualification"
                      value={detailFormData.qualification}
                      onChange={(v) => { setDetailFormData({ ...detailFormData, qualification: v }); clearFieldError("qualification"); }}
                      disabled={!isEditingDetail}
                      error={isEditingDetail ? formErrors.qualification : ""}
                    />
                    <FormField
                      label="MP Number"
                      id="detail_mp_number"
                      value={detailFormData.mp_number}
                      onChange={(v) => { setDetailFormData({ ...detailFormData, mp_number: v }); clearFieldError("mp_number"); }}
                      disabled={!isEditingDetail}
                      placeholder={isEditingDetail ? "Required for new doctors" : ""}
                      error={isEditingDetail ? formErrors.mp_number : ""}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <FormField
                      label="Medibase"
                      id="detail_medibase"
                      value={detailFormData.medibase}
                      onChange={(v) => setDetailFormData({ ...detailFormData, medibase: v })}
                      disabled={!isEditingDetail}
                    />
                    <FormField
                      label="Goodx ID"
                      id="detail_goodx_id"
                      value={detailFormData.goodx_id}
                      onChange={(v) => setDetailFormData({ ...detailFormData, goodx_id: v })}
                      disabled={!isEditingDetail}
                    />
                  </div>
                  {isEditingDetail ? (
                    <LocationSelector
                      key={selectedDoctor?.doctor_id}
                      city={detailFormData.city}
                      area={detailFormData.area}
                      suburb={detailFormData.suburb}
                      onLocationChange={(location) => {
                        setDetailFormData({
                          ...detailFormData,
                          city: location.city,
                          area: location.area,
                          suburb: location.suburb
                        });
                        clearFieldError("suburb");
                      }}
                      required={true}
                      showError={!!formErrors.suburb}
                      data-testid="doctor-detail-location-selector"
                    />
                  ) : (
                    <div className="space-y-1">
                      <Label className="text-xs text-muted-foreground">Location</Label>
                      <p className="text-sm">
                        {detailFormData.city && detailFormData.area && detailFormData.suburb
                          ? `${detailFormData.city} / ${detailFormData.area} / ${detailFormData.suburb}`
                          : detailFormData.area || "—"}
                      </p>
                    </div>
                  )}
                  <div className="grid grid-cols-2 gap-3">
                    {isEditingDetail ? (
                      <div className="space-y-1">
                        <Label className="text-xs text-muted-foreground">Assigned Rep <span className="text-red-500">*</span></Label>
                        <Select
                          value={detailFormData.assigned_rep}
                          onValueChange={(v) => { setDetailFormData({ ...detailFormData, assigned_rep: v }); clearFieldError("assigned_rep"); }}
                        >
                          <SelectTrigger className={`h-9 ${formErrors.assigned_rep ? "border-red-500" : ""}`}>
                            <SelectValue placeholder="Select rep" />
                          </SelectTrigger>
                          <SelectContent>
                            {REPS.map((rep) => (
                              <SelectItem key={rep} value={rep}>{getRepDisplayName(rep)}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {formErrors.assigned_rep && <p className="text-xs text-red-500 mt-1">{formErrors.assigned_rep}</p>}
                      </div>
                    ) : (
                      <FormField
                        label="Assigned Rep"
                        id="detail_assigned_rep"
                        value={getRepDisplayName(detailFormData.assigned_rep)}
                        onChange={() => {}}
                        disabled
                      />
                    )}
                  </div>
                </div>

                <Separator />

                {/* Practice Information */}
                <div className="space-y-3">
                  <SectionHeader icon={Building} title="Practice Information" />
                  <div className="grid grid-cols-2 gap-3">
                    <FormField
                      label="Practice Name"
                      id="detail_practice_name"
                      value={detailFormData.practice_name}
                      onChange={(v) => { setDetailFormData({ ...detailFormData, practice_name: v }); clearFieldError("practice_name"); }}
                      disabled={!isEditingDetail}
                      required
                      error={isEditingDetail ? formErrors.practice_name : ""}
                    />
                    <FormField
                      label="Practice Number"
                      id="detail_practice_number"
                      value={detailFormData.practice_number}
                      onChange={(v) => { setDetailFormData({ ...detailFormData, practice_number: v }); clearFieldError("practice_number"); }}
                      disabled={!isEditingDetail}
                      error={isEditingDetail ? formErrors.practice_number : ""}
                    />
                  </div>
                </div>

                <Separator />

                {/* Contact Information */}
                <div className="space-y-3">
                  <SectionHeader icon={Phone} title="Contact Information" />
                  <div className="grid grid-cols-2 gap-3">
                    <FormField
                      label="Office Phone"
                      id="detail_doctor_phone"
                      type="tel"
                      value={detailFormData.doctor_phone}
                      onChange={(v) => setDetailFormData({ ...detailFormData, doctor_phone: v })}
                      disabled={!isEditingDetail}
                      required
                    />
                    <FormField
                      label="Cell Number"
                      id="detail_cell_number"
                      type="tel"
                      value={detailFormData.cell_number}
                      onChange={(v) => { setDetailFormData({ ...detailFormData, cell_number: v }); clearFieldError("cell_number"); }}
                      disabled={!isEditingDetail}
                      error={isEditingDetail ? formErrors.cell_number : ""}
                    />
                  </div>
                  <div className="space-y-1">
                    <FormField
                      label="Email"
                      id="detail_doctor_email"
                      type="email"
                      value={detailFormData.doctor_email}
                      onChange={(v) => { setDetailFormData({ ...detailFormData, doctor_email: v }); clearFieldError("doctor_email"); }}
                      disabled={!isEditingDetail}
                      required
                      error={isEditingDetail ? formErrors.doctor_email : ""}
                    />
                    {selectedDoctor?.shared_practice_email && (
                      <p className="text-xs text-purple-600 flex items-center gap-1">
                        <Users className="w-3 h-3" />
                        Shared practice email detected
                      </p>
                    )}
                  </div>
                </div>

                <Separator />

                {/* Address */}
                <div className="space-y-3">
                  <SectionHeader icon={MapPin} title="Address" />
                  <FormField
                    label="Physical Address"
                    id="detail_address"
                    value={detailFormData.address}
                    onChange={(v) => { setDetailFormData({ ...detailFormData, address: v }); clearFieldError("address"); }}
                    disabled={!isEditingDetail}
                    error={isEditingDetail ? formErrors.address : ""}
                  />
                  <FormField
                    label="Postal Address"
                    id="detail_postal_address"
                    value={detailFormData.postal_address}
                    onChange={(v) => setDetailFormData({ ...detailFormData, postal_address: v })}
                    disabled={!isEditingDetail}
                  />
                </div>

                {/* Status and Meta */}
                <Separator />
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-muted-foreground">Doctor ID</span>
                    <span className="text-xs font-mono">{selectedDoctor.doctor_id}</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-muted-foreground">Status</span>
                    <Badge 
                      variant="secondary" 
                      className={selectedDoctor.active ? "bg-emerald-100 text-emerald-800" : "bg-slate-100 text-slate-600"}
                    >
                      {selectedDoctor.active ? "Active" : "Inactive"}
                    </Badge>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-muted-foreground">Source</span>
                    <span className="text-xs">{selectedDoctor.source || "Manual"}</span>
                  </div>
                </div>
              </div>
            </ScrollArea>
          )}
        </SheetContent>
      </Sheet>

      {/* "Doctor created — return to where you were?" (New Doctor redirect workflow) */}
      <Dialog open={Boolean(createdDoctor)} onOpenChange={(open) => { if (!open) setCreatedDoctor(null); }}>
        <DialogContent className="max-w-sm" data-testid="doctor-created-return-dialog">
          <DialogHeader>
            <DialogTitle>Doctor created</DialogTitle>
          </DialogHeader>
          <div className="rounded-md border bg-muted/40 p-3 mb-3">
            <p className="font-semibold text-sm">{createdDoctor?.name}</p>
            {createdDoctor?.area && (
              <p className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
                <MapPin className="w-3 h-3" />
                {createdDoctor.area}
              </p>
            )}
          </div>
          <p className="text-sm text-muted-foreground mb-4">
            {returnContext === "calendar"
              ? "Return to plan your appointment with this doctor selected?"
              : "Return to your entry with this doctor selected?"}
          </p>
          <div className="flex flex-col sm:flex-row gap-2 sm:justify-end">
            <Button
              variant="outline"
              onClick={() => setCreatedDoctor(null)}
              data-testid="doctor-created-stay"
            >
              Stay on Doctors
            </Button>
            <Button
              onClick={() => {
                const url = createdDoctor.url;
                setCreatedDoctor(null);
                navigate(url);
              }}
              data-testid="doctor-created-return"
            >
              {returnContext === "calendar" ? "Return to appointment" : "Return to log entry"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DoctorsPage;
